import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PersonIcon from "@material-ui/icons/Person";
import RoomIcon from "@material-ui/icons/Room";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import A_DatePicker from "../../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Select from "../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import * as toastActions from "../../../../../../store/actions/toastActions";
import * as currentPageActions from "../../../../../../store/actions/currentPageActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import {
  ALL_FIELDS,
  BOOLEAN_OPTIONS,
  BORROWER_STEPS,
  REGISTERED_TO_OPERATING_ADDRESS_MAP,
  buildInitialState,
  buildBorrowerOptionSetObject,
  buildBorrowerStateFromSource,
  clearBorrowerDraft,
  copyRegisteredAddressToOperating,
  ExecuteSqlStoreProc,
  getBorrowerDraftStorageKey,
  getRecordSource,
  hasDraftableBorrowerData,
  INDIVIDUAL_ONLY_FIELD_KEYS,
  isBorrowerFieldRequired,
  isNumericBorrowerFieldType,
  isOperatingAddressSynced,
  loadBorrowerDraft,
  NON_INDIVIDUAL_ONLY_FIELD_KEYS,
  parseBorrowerApiErrors,
  sanitizeBorrowerDraft,
  saveBorrowerDraft,
  shouldRenderBorrowerField,
  toOptionLabel,
  validateBorrowerStep,
} from "./Borrower_Form_helper";
import { httpPost, httpPut } from "../../../../../../Utils/apis/apis";
import LookupSearchSelect from "../Add_Loan/LookupSearchSelect";
import EgSearchField from "../Add_Loan/EgSearchField";
import { message } from "../../../../../../Utils/config/messages";
import { getCRMContactInfo } from "../../../../../../Utils/Helpers/common";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import useStyles from "./Add_Borrower.styles";

const SECTION_META = {
  Identity: {
    icon: <PersonIcon />,
    subtitle: "Borrower type and legal details",
  },
  Address: { icon: <RoomIcon />, subtitle: "Registered and operating address" },
  Compliance: {
    icon: <VerifiedUserIcon />,
    subtitle: "Tax, screening and consents",
  },
  Contact: { icon: <ContactMailIcon />, subtitle: "Primary contact details" },
};

const sectionDomId = (title) =>
  `borrower-section-${title.replace(/\s+/g, "-").toLowerCase()}`;

const CHIP_SELECT_FIELD_KEYS = ["BorrowerType", "PreferredContactMethod"];

const isChipSelectField = (field) =>
  field.type === "boolean" || CHIP_SELECT_FIELD_KEYS.includes(field.backendKey);

const AddBorrowerComponent = ({
  formConfig,
  isEdit,
  isEditForm,
  setToast,
  onSaveSuccess,
  onClose,
  getCurrentPage,
}) => {
  const classes = useStyles();
  const { pageUrl, crmId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [expandedSections, setExpandedSections] = useState(() =>
    BORROWER_STEPS.map((step) => step.title)
  );
  const [formData, setFormData] = useState(() => buildInitialState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [enumoptions, setEnumOptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [
    isOperatingAddressSameAsRegistered,
    setIsOperatingAddressSameAsRegistered,
  ] = useState(false);
  const [availableDraft, setAvailableDraft] = useState(null);
  const [isDraftBootstrapComplete, setIsDraftBootstrapComplete] =
    useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [fieldRenderKey, setFieldRenderKey] = useState(0);
  const effectiveEditMode = isEdit || isEditForm;
  let { crmContactUserEmail } = getCRMContactInfo();
  const LoggedInUserEmail = useSelector((state) =>
    state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      ? state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      : crmContactUserEmail
  );
  const canUseDrafts = !effectiveEditMode;
  const draftOwnerIdentifier =
    LoggedInUserEmail || crmContactUserEmail || "anonymous";
  const draftStorageKey = canUseDrafts
    ? getBorrowerDraftStorageKey(draftOwnerIdentifier)
    : null;
  const borrowerType = (formData.BorrowerType || "").toLowerCase();

  const isIndividual = ["individual", "sole trader"].includes(borrowerType);
  const isNonIndividual = !isIndividual && borrowerType !== "";

  const getStepFields = (step) =>
    step.sections
      ? step.sections.flatMap((section) => section.fields)
      : step.fields || [];

  useEffect(() => {
    getEnumOptionsForField();
    if (!effectiveEditMode) {
      setFormData(buildInitialState());
      setIsOperatingAddressSameAsRegistered(false);
      return;
    }
    const source = getRecordSource(formConfig);
    setFormStateFromSource(source);
  }, [effectiveEditMode, formConfig]);

  useEffect(() => {
    let isMounted = true;

    const fetchDraft = async () => {
      if (!canUseDrafts || !draftStorageKey) {
        if (isMounted) {
          setAvailableDraft(null);
          setIsDraftBootstrapComplete(true);
        }
        return;
      }

      const loadedDraft = await loadBorrowerDraft(draftStorageKey);
      const draft = sanitizeBorrowerDraft(loadedDraft);

      if (!isMounted) {
        return;
      }

      setAvailableDraft(
        draft &&
          hasDraftableBorrowerData(
            draft.formData,
            draft.activeStep,
            draft.isOperatingAddressSameAsRegistered
          )
          ? draft
          : null
      );
      setIsDraftBootstrapComplete(true);
    };

    fetchDraft();

    return () => {
      isMounted = false;
    };
  }, [canUseDrafts, draftStorageKey]);

  const setFormStateFromSource = (source) => {
    const newState = buildBorrowerStateFromSource(source);
    setFormData(newState);
    setIsOperatingAddressSameAsRegistered(isOperatingAddressSynced(newState));
  };

  const dismissAvailableDraft = useCallback(() => {
    if (availableDraft) {
      setAvailableDraft(null);
    }
  }, [availableDraft]);

  useEffect(() => {
    const hiddenFieldKeys = isNonIndividual
      ? INDIVIDUAL_ONLY_FIELD_KEYS
      : isIndividual
      ? NON_INDIVIDUAL_ONLY_FIELD_KEYS
      : [];

    if (hiddenFieldKeys.length === 0) {
      return;
    }

    setFormData((prev) =>
      hiddenFieldKeys.reduce(
        (updated, backendKey) => {
          updated[backendKey] = "";
          return updated;
        },
        { ...prev }
      )
    );
    setErrors((prevErrors) =>
      hiddenFieldKeys.reduce(
        (nextErrors, backendKey) => {
          nextErrors[backendKey] = "";
          return nextErrors;
        },
        { ...prevErrors }
      )
    );
  }, [borrowerType]);

  const getEnumOptionsForField = async () => {
    const opionsetCategories = "BorrowerType,Country,PreferredContactMethod";
    try {
      setIsLoading(true);
      const payload = [
        {
          id: "Borrower_Dropdowns",
          spName: "[DF].[usp_GetOptionSetsByCategories]",
          parameters: {
            Categories: opionsetCategories,
          },
        },
      ];
      const response = await ExecuteSqlStoreProc(payload);
      const data = response?.Borrower_Dropdowns || [];
      const categories = opionsetCategories.split(",").map((c) => c.trim());
      const optionSetObject = buildBorrowerOptionSetObject(categories, data);
      if (Object.keys(optionSetObject).length !== 0) {
        setEnumOptions(optionSetObject);
      } else {
        setEnumOptions([]);
      }
    } catch (error) {
      setEnumOptions([]);
    } finally {
      setIsLoading(false);
      setFieldRenderKey((prevState) => prevState + 1);
    }
  };

  const getEnumOptions = (field) => {
    if (field.type === "boolean") {
      return BOOLEAN_OPTIONS;
    }
    const optionsFromEnum =
      field.backendKey === "OperatingAddressCountry"
        ? enumoptions["Country"]
        : enumoptions[field.backendKey] || [];
    if (optionsFromEnum.length === 0) {
      return [];
    }
    return optionsFromEnum
      .map((data) => ({
        id: data?.id,
        label: toOptionLabel(data?.label),
        value: data?.value,
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  };

  const clearFieldError = (fieldId) => {
    setErrors((prev) => ({
      ...prev,
      [fieldId]: "",
    }));
  };

  const handleTextChange = (event) => {
    const { id, value } = event.target;
    dismissAvailableDraft();
    setFormData((prevState) => {
      const nextState = {
        ...prevState,
        [id]: value,
      };
      if (
        isOperatingAddressSameAsRegistered &&
        REGISTERED_TO_OPERATING_ADDRESS_MAP[id]
      ) {
        nextState[REGISTERED_TO_OPERATING_ADDRESS_MAP[id]] = value;
      }
      return nextState;
    });
    clearFieldError(id);
  };

  const handleDateChange = (value, id) => {
    dismissAvailableDraft();
    setFormData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
    clearFieldError(id);
  };

  const handleSelectChange = (selectedOption, id) => {
    dismissAvailableDraft();
    setFormData((prevState) => ({
      ...prevState,
      [id]: selectedOption?.value || "",
    }));
    clearFieldError(id);
  };

  const handleExposureGroupChange = (value, id) => {
    dismissAvailableDraft();
    setFormData((prevState) => ({
      ...prevState,
      [id]: value || "",
    }));
    clearFieldError(id);
  };

  const handleLookupChange = (selected, id) => {
    const selectedValue = selected?.value || "";
    dismissAvailableDraft();
    setFormData((prevState) => {
      const nextState = {
        ...prevState,
        [id]: selectedValue,
      };
      if (
        isOperatingAddressSameAsRegistered &&
        REGISTERED_TO_OPERATING_ADDRESS_MAP[id]
      ) {
        nextState[REGISTERED_TO_OPERATING_ADDRESS_MAP[id]] = selectedValue;
      }
      return nextState;
    });
    clearFieldError(id);
  };

  const handleOperatingAddressSyncChange = (event) => {
    const checked = event.target.checked;
    dismissAvailableDraft();
    setIsOperatingAddressSameAsRegistered(checked);

    if (!checked) {
      return;
    }

    setFormData((prevState) => copyRegisteredAddressToOperating(prevState));
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      Object.values(REGISTERED_TO_OPERATING_ADDRESS_MAP).forEach(
        (operatingKey) => {
          nextErrors[operatingKey] = "";
        }
      );
      return nextErrors;
    });
  };

  const getFielderrorMessage = (field) => {
    if (errors[field.backendKey]) {
      return errors[field.backendKey];
    }
    return "";
  };

  const validateAllSteps = () => {
    const allErrors = BORROWER_STEPS.reduce((acc, step) => {
      const stepErrors = validateBorrowerStep({
        currentStep: step,
        formData,
        isIndividual,
        isNonIndividual,
      });
      return { ...acc, ...stepErrors };
    }, {});
    setErrors(allErrors);
    return allErrors;
  };

  const focusField = (backendKey) => {
    window.setTimeout(() => {
      const element =
        document.getElementById(backendKey) ||
        document.querySelector(`[name="${backendKey}"]`);
      if (element?.scrollIntoView) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (element?.focus) {
        element.focus();
      }
    }, 80);
  };

  const handleAccordionToggle = (sectionTitle) => {
    setExpandedSections((prevState) =>
      prevState.includes(sectionTitle)
        ? prevState.filter((title) => title !== sectionTitle)
        : [...prevState, sectionTitle]
    );
  };

  const ensureSectionExpanded = (sectionTitle) => {
    setExpandedSections((prevState) =>
      prevState.includes(sectionTitle)
        ? prevState
        : [...prevState, sectionTitle]
    );
  };

  const shouldRenderField = (field) =>
    shouldRenderBorrowerField(field, isIndividual, isNonIndividual);

  const isFieldRequired = (field) =>
    isBorrowerFieldRequired(field, isIndividual, isNonIndividual);

  const handleRestoreDraft = useCallback(() => {
    if (!availableDraft) {
      return;
    }

    setFormData(availableDraft.formData);
    setIsOperatingAddressSameAsRegistered(
      availableDraft.isOperatingAddressSameAsRegistered
    );
    setActiveStep(
      Math.min(
        Math.max(availableDraft.activeStep || 0, 0),
        BORROWER_STEPS.length - 1
      )
    );
    setErrors({});
    setFieldRenderKey((prevState) => prevState + 1);
    setAvailableDraft(null);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.BORROWER_DRAFT_RESTORED,
      status: true,
      duration: 3000,
    });
  }, [availableDraft, setToast]);

  const handleDiscardDraft = useCallback(async () => {
    if (draftStorageKey) {
      await clearBorrowerDraft(draftStorageKey);
    }

    setAvailableDraft(null);
    setFormData(buildInitialState());
    setIsOperatingAddressSameAsRegistered(false);
    setActiveStep(0);
    setErrors({});
    setFieldRenderKey((prevState) => prevState + 1);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.BORROWER_DRAFT_DISCARDED,
      status: true,
      duration: 3000,
    });
  }, [draftStorageKey, setToast]);

  const saveDraft = useCallback(
    async ({
      showEmptyWarning = true,
      successMessage = "Borrower draft saved.",
    } = {}) => {
      if (
        !canUseDrafts ||
        !draftStorageKey ||
        !isDraftBootstrapComplete ||
        isDraftSaving
      ) {
        return { saved: false, skipped: true };
      }

      if (
        !hasDraftableBorrowerData(
          formData,
          activeStep,
          isOperatingAddressSameAsRegistered
        )
      ) {
        if (showEmptyWarning) {
          setToast({
            type: toastSeverity.ERROR,
            severity: toastSeverity.ERROR,
            message: message.BORROWER_DRAFT_EMPTY_WARNING,
            status: true,
            duration: 3000,
          });
        }
        return { saved: false, skipped: true, empty: true };
      }

      setIsDraftSaving(true);

      const saved = await saveBorrowerDraft(draftStorageKey, {
        version: 1,
        activeStep,
        formData,
        isOperatingAddressSameAsRegistered,
        updatedAt: new Date().toISOString(),
      });

      setIsDraftSaving(false);

      setToast({
        type: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        severity: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        message: saved ? successMessage : message.SOMETHING_WENT_WRONG,
        status: true,
        duration: saved ? 3000 : 5000,
      });

      if (saved) {
        setAvailableDraft(null);
      }

      return { saved, skipped: false };
    },
    [
      canUseDrafts,
      draftStorageKey,
      isDraftBootstrapComplete,
      isDraftSaving,
      formData,
      activeStep,
      isOperatingAddressSameAsRegistered,
      setToast,
    ]
  );

  const handleSaveDraft = useCallback(() => saveDraft(), [saveDraft]);

  const handleFinish = async () => {
    const allErrors = validateAllSteps();
    if (Object.keys(allErrors).length > 0) {
      const firstStepWithError = BORROWER_STEPS.find((step) =>
        getStepFields(step).some((field) => allErrors[field.backendKey])
      );
      if (firstStepWithError) {
        ensureSectionExpanded(firstStepWithError.title);
        const firstInvalidField = getStepFields(firstStepWithError).find(
          (field) => allErrors[field.backendKey]
        );
        if (firstInvalidField) {
          focusField(firstInvalidField.backendKey);
        }
      }
      return;
    }

    const payload = ALL_FIELDS.reduce((result, field) => {
      result[field.backendKey] = formData[field.backendKey];
      return result;
    }, {});

    setIsSubmitting(true);

    const source = getRecordSource(formConfig);
    const insertPayload = {
      id: effectiveEditMode
        ? source?._partitionKey ||
          source?.Id ||
          source?.id ||
          formData.LoanId ||
          formData.ApplicationId ||
          crmId ||
          null
        : null,
      label: "borrower",
      type: "vertex",
      properties: payload,
      edges: [],
      security: {
        read: { groupids: [], users: [] },
        update: { groupids: [], users: [] },
        full: { groupids: [], users: [] },
        privateRecord: true,
      },
      duplicateDetection: [["Postcode", "LegalName"]],
      submitToStoredProc: effectiveEditMode
        ? "DF.usp_EditBorrower"
        : "DF.usp_AddBorrower",
    };

    try {
      const response = effectiveEditMode
        ? await httpPut(
            "/HTBCRMData/update?validationType=borrower",
            insertPayload
          )
        : await httpPost(`/HTBCRMData/insert`, insertPayload);
      if (response && response.id != null) {
        if (canUseDrafts && draftStorageKey) {
          await clearBorrowerDraft(draftStorageKey);
        }
        if (effectiveEditMode) {
          setToast({
            type: toastSeverity.SUCCESS,
            severity: toastSeverity.SUCCESS,
            message: "Borrower has been updated successfully.",
            status: true,
            duration: 4000,
          });
          if (pageUrl && crmId && getCurrentPage) {
            getCurrentPage({ pageUrl, crmId });
          }
          if (onClose) {
            onClose();
          }
        } else if (onSaveSuccess) {
          onSaveSuccess(response.id);
        }
        return;
      }

      if (
        response?.response?.data?.errors &&
        Object.keys(response.response.data.errors).length > 0
      ) {
        const apiErrors = parseBorrowerApiErrors(response.response.data);
        setErrors((prev) => ({
          ...prev,
          ...apiErrors,
        }));
        const errorMessages = Object.values(apiErrors).join(". ");
        setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessages,
          status: true,
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      const errorMessage =
        response?.response?.data?.Message ||
        "Failed to save Borrower. Please try again.";
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: errorMessage,
        status: true,
        duration: 5000,
      });
    } catch (error) {
      const errorResponse = error?.response?.data;
      if (
        errorResponse?.errors &&
        Object.keys(errorResponse.errors).length > 0
      ) {
        const apiErrors = parseBorrowerApiErrors(errorResponse);
        setErrors((prev) => ({
          ...prev,
          ...apiErrors,
        }));
        const errorMessages = Object.values(apiErrors).join(". ");
        setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessages,
          status: true,
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: "Failed to save Borrower. Please try again.",
        status: true,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoading = () => (
    <div
      style={{
        height: "72vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <CircularProgress />
      <Typography variant="body2">Loading form...</Typography>
    </div>
  );

  const EDIT_LOCKED_FIELDS = ["BorrowerType", "LegalName"];
  const isFieldDisabled = (field) =>
    effectiveEditMode && EDIT_LOCKED_FIELDS.includes(field.backendKey);

  const renderChipSelect = (field) => {
    const options = getEnumOptions(field);
    const currentValue = formData[field.backendKey];
    const required = isFieldRequired(field);
    const errorText = getFielderrorMessage(field);
    const locked = isFieldDisabled(field);

    return (
      <div
        className={classes.chipSelectWrap}
        id={field.backendKey}
        key={`${field.backendKey}-${fieldRenderKey}-${currentValue}`}
      >
        <Typography className={classes.chipSelectLabel}>
          {field.label}
          {required ? (
            <span className={classes.chipSelectRequired}> *</span>
          ) : null}
        </Typography>
        <div className={classes.chipSelectOptions}>
          {options.map((option) => {
            const optionValue = `${option.value}`;
            const selected = `${currentValue}` === optionValue;
            return (
              <Chip
                key={`${field.backendKey}-${optionValue}`}
                label={option.label}
                size="small"
                clickable={!locked}
                disabled={locked}
                onClick={
                  locked
                    ? undefined
                    : () => {
                        dismissAvailableDraft();
                        setFormData((prevState) => ({
                          ...prevState,
                          [field.backendKey]:
                            selected && !required ? "" : option.value,
                        }));
                        clearFieldError(field.backendKey);
                      }
                }
                className={`${classes.selectChip} ${
                  selected ? classes.selectChipActive : ""
                }`}
              />
            );
          })}
        </div>
        {errorText ? (
          <Typography
            className={classes.chipSelectHelper}
            style={{ color: "#f44336" }}
          >
            {errorText}
          </Typography>
        ) : null}
      </div>
    );
  };

  const renderField = (field) => {
    if (isChipSelectField(field)) {
      return renderChipSelect(field);
    }
    if (field.type === "exposure-group-search") {
      return (
        <EgSearchField
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          value={formData[field.backendKey] || ""}
          onChange={(val) => handleExposureGroupChange(val, field.backendKey)}
          required={isFieldRequired(field)}
          error={Boolean(errors[field.backendKey])}
          helperText={getFielderrorMessage(field)}
          fetchUrl="/ExposureGroup/getall"
          createUrl="/exposuregroup/addgroup"
          createPayloadKey="name"
          displayKey="name"
        />
      );
    }
    if (field.type === "enum" || field.type === "boolean") {
      const options = getEnumOptions(field);
      return (
        <A_Select
          key={`${field.backendKey}-${fieldRenderKey}-${
            formData[field.backendKey]
          }`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          required={isFieldRequired(field)}
          defaultValue={formData[field.backendKey]}
          options={options}
          onChange={handleSelectChange}
          error={Boolean(errors[field.backendKey])}
          // fieldHelperText={getFieldHelperText(field)}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    if (field.type === "lookup") {
      const options = getEnumOptions(field);
      return (
        <LookupSearchSelect
          key={`${field.backendKey}-${fieldRenderKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          required={isFieldRequired(field)}
          defaultValue={formData[field.backendKey]}
          options={options}
          multiple={!!field.multiple}
          onChange={handleLookupChange}
          // fieldHelperText={getFieldHelperText(field)}
          error={Boolean(errors[field.backendKey])}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    if (field.type === "date") {
      return (
        <A_DatePicker
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          margin="dense"
          placeholder="DD/MM/YYYY"
          inputVariant="outlined"
          InputLabelProps={{ shrink: true }}
          defaultValue={formData[field.backendKey] || null}
          onChange={handleDateChange}
          required={isFieldRequired(field)}
          error={Boolean(errors[field.backendKey])}
          // fieldHelperText={getFieldHelperText(field)}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    const isNumericField = isNumericBorrowerFieldType(field.type);
    return (
      <A_TextField
        key={`${field.backendKey}-${fieldRenderKey}`}
        id={field.backendKey}
        name={field.backendKey}
        label={field.label}
        variant="outlined"
        type={isNumericField ? "number" : undefined}
        fullWidth
        InputLabelProps={{ shrink: true }}
        defaultValue={formData[field.backendKey]}
        inputType="value"
        multiline={field.multiline}
        rows={field.rows}
        onChange={handleTextChange}
        required={isFieldRequired(field)}
        disabled={isFieldDisabled(field)}
        error={Boolean(errors[field.backendKey])}
        fieldHelperText={field.fieldHelperText}
        adornment={field.adornment}
        inputProps={{
          ...(field.placeholder ? { placeholder: field.placeholder } : {}),
          ...(isNumericField
            ? { min: 0, step: field.type === "int" ? 1 : "any" }
            : {}),
        }}
        helperText={getFielderrorMessage(field)}
      />
    );
  };

  const renderFieldGrid = (field) => (
    <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.backendKey}>
      {renderField(field)}
    </Grid>
  );

  const renderStepBody = (step) => {
    if (step.sections) {
      return step.sections.map((section) => (
        <div key={section.title} className={classes.subSectionWrap}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Typography className={classes.subSectionTitle}>
              {section.title}
            </Typography>
            {section.title === "Operating Address" ? (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={isOperatingAddressSameAsRegistered}
                    onChange={handleOperatingAddressSyncChange}
                  />
                }
                label="Same as Registered Address"
                style={{ marginRight: 0 }}
              />
            ) : null}
          </div>
          <Grid container spacing={2}>
            {section.fields
              .filter((field) => shouldRenderField(field))
              .map((field) => renderFieldGrid(field))}
          </Grid>
        </div>
      ));
    }

    return (
      <Grid container spacing={2}>
        {step.fields
          .filter((field) => shouldRenderField(field))
          .map((field) => renderFieldGrid(field))}
      </Grid>
    );
  };

  if (isLoading) {
    return renderLoading();
  }

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.formCard}>
        <div className={classes.fieldsContainer}>
          {availableDraft ? (
            <div className={classes.draftBanner}>
              <div className={classes.draftBannerContent}>
                <Typography className={classes.draftBannerTitle}>
                  Unsaved draft found
                </Typography>
                <Typography className={classes.draftBannerText}>
                  {availableDraft.updatedAt
                    ? `A borrower draft saved on ${new Date(
                        availableDraft.updatedAt
                      ).toLocaleString()} is available for your account.`
                    : "A previous borrower draft is available for your account."}
                </Typography>
              </div>
              <div className={classes.draftBannerActions}>
                <A_Button
                  label="Start fresh"
                  premiumVariant="secondary"
                  onClick={handleDiscardDraft}
                  disabled={isSubmitting || isLoading}
                />
                <A_Button
                  label="Resume draft"
                  premiumVariant="primary"
                  onClick={handleRestoreDraft}
                  disabled={isSubmitting || isLoading}
                />
              </div>
            </div>
          ) : null}

          {BORROWER_STEPS.map((step) => {
            const sectionMeta = SECTION_META[step.title] || {};

            return (
              <Accordion
                key={step.title}
                id={sectionDomId(step.title)}
                expanded={expandedSections.includes(step.title)}
                onChange={() => handleAccordionToggle(step.title)}
                className={classes.accordion}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      style={{ color: "var(--color-text-primary)" }}
                    />
                  }
                  className={classes.accordionSummary}
                >
                  {sectionMeta.icon ? (
                    <div className={classes.summaryIconBox}>
                      {sectionMeta.icon}
                    </div>
                  ) : null}
                  <div className={classes.summaryTextWrap}>
                    <span className={classes.summaryTitle}>{step.title}</span>
                    {sectionMeta.subtitle ? (
                      <span className={classes.summarySubtitle}>
                        {sectionMeta.subtitle}
                      </span>
                    ) : null}
                  </div>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <div className={classes.accordionContent}>
                    {renderStepBody(step)}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
        <div className={classes.actions}>
          <span />
          <div className={classes.actionGroup}>
            {canUseDrafts ? (
              <A_Button
                label={
                  isDraftSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Save Draft"
                  )
                }
                premiumVariant="secondary"
                onClick={handleSaveDraft}
                disabled={
                  isSubmitting || isDraftSaving || !isDraftBootstrapComplete
                }
                className={classes.actionButton}
              />
            ) : null}
            <A_Button
              label="Finish"
              premiumVariant="primary"
              onClick={handleFinish}
              disabled={isSubmitting}
              className={classes.actionButton}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            />
          </div>
        </div>
      </Paper>
    </div>
  );
};

export const Add_Borrower = AddBorrowerComponent;

AddBorrowerComponent.propTypes = {
  formConfig: PropTypes.any,
  isEdit: PropTypes.bool,
  isEditForm: PropTypes.bool,
  setToast: PropTypes.func,
  onClose: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  getCurrentPage: PropTypes.func,
};

AddBorrowerComponent.defaultProps = {
  formConfig: null,
  isEdit: false,
  isEditForm: false,
  setToast: () => {},
  onClose: null,
  onSaveSuccess: null,
  getCurrentPage: null,
};

Add_Borrower.propTypes = AddBorrowerComponent.propTypes;
Add_Borrower.defaultProps = AddBorrowerComponent.defaultProps;

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    Object.assign({}, toastActions, currentPageActions),
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps, null)(Add_Borrower);
